import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Copy, Loader2, Info, Check } from "lucide-react";
import { reviewerApi, apiBaseConfigured } from "@/lib/api";
import { toast } from "@/hooks/use-toast";
import { QRCodeSVG } from "qrcode.react";
import { FOLLOW_UP_REASONS } from "@/lib/followUpReasons";
import { useOperatorVertical } from "@/hooks/useOperatorMe";
import { workflowChoicesFor, workflowLabel } from "@/lib/workflowChoices";
import {
  collectsDateOfBirth,
  copyForVertical,
  showsFollowUpReason,
} from "@/lib/verticalCopy";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const reasons = FOLLOW_UP_REASONS;


type Step = "form" | "sent";

/** Auto-format raw digits into MM/DD/YYYY as the user types. */
function formatDobInput(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
}


interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a session is successfully created so callers can refresh their list. */
  onCreated?: () => void;
}

export function AddApplicantDialog({ open, onOpenChange, onCreated }: Props) {
  // Cache read on the shared `operator-me` key — the dialog's hosts render
  // inside <ProtectedRoute>.
  const { vertical, me } = useOperatorVertical();
  const copy = copyForVertical(vertical);
  const showsDob = collectsDateOfBirth(vertical);
  const showsReason = showsFollowUpReason(vertical);
  // The flows this org has definitions for. EMPTY IS THE NORMAL CASE — an org
  // with one journey has nothing to choose, and a dropdown holding a single
  // entry is a control that cannot be used. Every leasing org is in that state
  // today, so for them this change is invisible.
  const workflowChoices = workflowChoicesFor(me);
  const showsWorkflowPicker = workflowChoices.length > 0;

  const [step, setStep] = useState<Step>("form");
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [email, setEmail] = useState("");
  const [appliedFor, setAppliedFor] = useState("");
  const [reason, setReason] = useState<string>("");
  // "" is unselected — the same shape `reason` uses above, and what makes the
  // trigger fall back to its placeholder instead of naming a non-answer.
  const [workflowKey, setWorkflowKey] = useState<string>("");
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [magicLink, setMagicLink] = useState<string>("");
  const [expiresAt, setExpiresAt] = useState<string | undefined>(undefined);

  const reset = () => {
    setStep("form");
    setName("");
    setDob("");
    setEmail("");
    setAppliedFor("");
    setReason("");
    setWorkflowKey("");
    setCopied(false);
    setSubmitting(false);
    setMagicLink("");
    setExpiresAt(undefined);
  };


  const handleSend = async () => {
    // Built from the copy deck rather than naming the leasing fields, so the
    // message cannot promise a "reason for follow-up" on a form that has no
    // reason field. Required set is name + email + locator, plus the reason
    // only where one exists.
    const missing =
      !name.trim() || !email.trim() || !appliedFor.trim() || (showsReason && !reason);
    if (missing) {
      const fields = [
        "name",
        "email",
        copy.locatorLabel.toLowerCase(),
        ...(showsReason ? ["reason for follow-up"] : []),
      ];
      toast({
        title: "Missing information",
        description: `Enter the ${copy.applicantNoun}'s ${fields.slice(0, -1).join(", ")}, and ${fields[fields.length - 1]}.`,
      });
      return;
    }
    // If the API isn't configured, fall back to the prior demo behavior.
    if (!apiBaseConfigured()) {
      setMagicLink("");
      setStep("sent");
      onCreated?.();
      return;
    }
    setSubmitting(true);
    try {
      const result = await reviewerApi.createSession({
        applicant_name: name,
        email,
        // Leasing-only, and nullable server-side. Recruiting sends nothing
        // rather than a placeholder value — the concept does not exist there.
        reason: showsReason ? reason : undefined,
        dob: showsDob ? dob.trim() || undefined : undefined,
        applied_for: appliedFor.trim() || undefined,
        // OMITTED, not sent as undefined. `JSON.stringify` would drop an
        // undefined value anyway, but "the key is absent" is the contract this
        // dialog has with the API — asserting it is the difference between a
        // test that checks the shape and one that checks a serialisation
        // side effect.
        //
        // An unknown key is a 400 server-side, never a silent fallback, so only
        // values that came from `org.workflows` are ever sent.
        //
        // Gated on `showsWorkflowPicker` as well as the value: the state
        // outlives the control. If `workflows` empties while the dialog is
        // open — a definition removed, the query refetched — the picker
        // unmounts with a selection still held, and sending it would 400 with
        // no field on screen to explain why.
        ...(showsWorkflowPicker && workflowKey ? { workflow_key: workflowKey } : {}),
      });

      setMagicLink(result.magic_link);
      setExpiresAt(result.expires_at);
      setStep("sent");
      onCreated?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not create the session.";
      toast({ title: "Couldn't send invitation", description: message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(reset, 300);
  };

  const handleCopyLink = async () => {
    if (magicLink) {
      try {
        await navigator.clipboard.writeText(magicLink);
      } catch {
        // ignore — fallback below
      }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (step === "sent") {
    // Compute "expires in N days" copy. Fallback to 7 days when backend omits.
    const expiryDays = (() => {
      if (!expiresAt) return 7;
      const ms = new Date(expiresAt).getTime() - Date.now();
      if (!Number.isFinite(ms) || ms <= 0) return 7;
      return Math.max(1, Math.ceil(ms / (1000 * 60 * 60 * 24)));
    })();

    // Masked link preview (e.g. "diga.vouch/v/EYhD…SKA")
    const maskedLink = (() => {
      if (!magicLink) return "Link unavailable";
      try {
        const url = new URL(magicLink);
        const path = url.pathname.replace(/^\/+/, "");
        const last = path.split("/").pop() ?? "";
        const head = last.slice(0, 4);
        const tail = last.slice(-3);
        const prefix = path.includes("/") ? path.slice(0, path.lastIndexOf("/") + 1) : "";
        return `${url.host}/${prefix}${head}${last.length > 7 ? "…" : ""}${last.length > 7 ? tail : ""}`;
      } catch {
        return magicLink.length > 48 ? `${magicLink.slice(0, 32)}…${magicLink.slice(-6)}` : magicLink;
      }
    })();


    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="w-[calc(100vw-1rem)] max-w-[400px] sm:max-w-[400px] rounded-2xl sm:rounded-3xl border-slate-200/80 shadow-[0_24px_64px_rgba(0,0,0,0.18)] p-5 sm:p-8 max-h-[calc(100dvh-1rem)] overflow-y-auto">
          <DialogTitle className="sr-only">Verification invite sent</DialogTitle>

          {/* 1. Success icon */}
          <div
            className="w-[60px] h-[60px] rounded-2xl flex items-center justify-center mx-auto"
            style={{ background: "#7C3AED" }}
          >
            <Check className="w-[26px] h-[26px] text-white" strokeWidth={2.75} />
          </div>

          {/* 2. Hero header */}
          <div className="text-center mt-5">
            <div className="text-[11px] sm:text-xs font-bold tracking-[0.08em] uppercase text-slate-400 mb-1">
              Verification invite sent
            </div>
            <div className="text-base sm:text-lg font-extrabold text-slate-900 break-all px-1 mb-2">
              {email || "applicant@example.com"}
            </div>
            <p className="text-[13px] text-slate-500 leading-relaxed">
              We'll notify you when the applicant completes verification.
            </p>
          </div>

          {/* 3. QR block */}
          <div className="mt-7">
            <div className="text-[11px] font-bold tracking-[0.08em] uppercase text-slate-400 mb-2.5">
              Scan to start verification
            </div>
            <div className="border-[1.5px] border-slate-200 rounded-[20px] p-6 sm:p-7 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.04)] flex items-center justify-center">
              <div className="w-[140px] h-[140px] min-[380px]:w-[172px] min-[380px]:h-[172px] flex items-center justify-center">
                {magicLink ? (
                  <QRCodeSVG
                    value={magicLink}
                    size={172}
                    level="M"
                    includeMargin={false}
                    fgColor="#0F172A"
                    className="w-full h-full"
                  />
                ) : (
                  <p className="text-xs text-slate-500 text-center px-3">
                    QR code unavailable — magic link not returned
                  </p>
                )}
              </div>
            </div>
            <p className="text-[13px] text-slate-500 text-center leading-snug mt-2.5">
              Applicant nearby? They can <span className="font-semibold text-slate-900">Scan to Apply</span> directly from this screen.
            </p>
          </div>

          {/* 4. Link block */}
          <div className="mt-6">
            <div className="text-[11px] font-bold tracking-[0.08em] uppercase text-slate-400 mb-2">
              Verification link
            </div>
            <div className="flex items-stretch border-[1.5px] border-slate-200 rounded-xl bg-slate-50 overflow-hidden">
              <input
                type="text"
                readOnly
                value={maskedLink}
                className="flex-1 min-w-0 bg-transparent px-3.5 py-3 text-[13px] text-slate-500 outline-none truncate cursor-default"
              />
              <button
                type="button"
                onClick={handleCopyLink}
                disabled={!magicLink}
                className="flex items-center gap-1.5 px-3 sm:px-4 bg-white border-l-[1.5px] border-slate-200 text-[13px] font-semibold text-[#7C3AED] hover:bg-[#F5F3FF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shrink-0"
              >
                <Copy className="w-3.5 h-3.5" />
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="text-xs text-slate-400 mt-2 pl-0.5">
              🔒 Link expires in {expiryDays} {expiryDays === 1 ? "day" : "days"}.
            </div>
          </div>

          {/* 5. Action stack */}
          <div className="flex flex-col gap-2.5 mt-6">
            <Button
              className="w-full h-12 rounded-xl text-white font-bold text-[15px] border-0 hover:brightness-110 transition-all"
              style={{ background: "#7C3AED" }}
              size="lg"
              onClick={reset}
            >
              Add Another Applicant
            </Button>
            <button
              type="button"
              className="w-full text-center text-sm font-medium text-slate-400 hover:text-slate-600 transition-colors py-1.5"
              onClick={handleClose}
            >
              Return to Dashboard
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-lg sm:max-w-lg rounded-2xl sm:rounded-3xl border-slate-200/80 shadow-[0_30px_80px_-20px_rgba(76,29,149,0.25)] p-4 sm:p-6 max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <DialogTitle className="sr-only">Send Request</DialogTitle>
        <div className="flex items-center gap-3 mb-4 sm:mb-5 pr-8">
          <div
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center shadow-[0_8px_20px_-6px_rgba(124,58,237,0.45)] shrink-0"
            style={{ background: "linear-gradient(135deg, #7C3AED, #6D28D9)" }}
          >
            <UserPlus className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">Send Request</h2>
            <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-[#6D28D9]">
              {copy.sendRequestEyebrow}
            </p>
          </div>
        </div>


        {/* Buildium import is hidden until that integration ships. */}

        <div className="space-y-4 sm:space-y-5">
          <div className="space-y-2">
            <Label className="text-xs font-semibold tracking-wider uppercase text-slate-700">
              Full Name <span className="text-red-500">*</span>
            </Label>
            <Input
              placeholder="e.g. Marcus Thorne"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-12 rounded-xl border-slate-200 bg-slate-50/60 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#7C3AED]/30 focus-visible:border-[#7C3AED]"
            />
          </div>

          {/*
            One column when there is no DOB, so the email field fills the row
            rather than leaving a gap where a dropped field used to be.
          */}
          <div className={showsDob ? "grid grid-cols-1 sm:grid-cols-2 gap-4" : "grid grid-cols-1 gap-4"}>
            {showsDob && (
            <div className="space-y-2">
              <Label className="text-xs font-semibold tracking-wider uppercase text-slate-700">
                Date of Birth <span className="text-red-500">*</span>
              </Label>
              <Input
                placeholder="MM / DD / YYYY"
                value={dob}
                onChange={(e) => setDob(formatDobInput(e.target.value))}
                className="h-12 rounded-xl border-slate-200 bg-slate-50/60 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#7C3AED]/30 focus-visible:border-[#7C3AED]"
              />
            </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs font-semibold tracking-wider uppercase text-slate-700">
                Email Address <span className="text-red-500">*</span>
              </Label>
              <Input
                type="email"
                placeholder="e.g. m.thorne@gmail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 rounded-xl border-slate-200 bg-slate-50/60 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#7C3AED]/30 focus-visible:border-[#7C3AED]"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold tracking-wider uppercase text-slate-700">
              {copy.locatorLabel} <span className="text-red-500">*</span>
            </Label>
            <Input
              placeholder={copy.locatorPlaceholder}
              value={appliedFor}
              onChange={(e) => setAppliedFor(e.target.value)}
              className="h-12 rounded-xl border-slate-200 bg-slate-50/60 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#7C3AED]/30 focus-visible:border-[#7C3AED]"
            />
          </div>

          {/*
            The flow this applicant walks (ADR-014). Rendered only when the org
            actually has definitions — see `workflowChoicesFor`.

            ⚠️ Phase-0 SCAFFOLDING, not a permanent field. The design record is
            explicit that the operator should end up seeing ONE control, not
            two: once campaigns exist, picking the role resolves the flow and
            this collapses into a derived, read-only line. It is here because
            there is nothing to derive from yet.

            Optional on purpose: an org with definitions can still want the
            standard journey for one applicant, and that is what every case got
            before this control existed. Left blank, no key is sent.

            ⚠️ That non-answer is a PLACEHOLDER, not a listed option. It used to
            be an explicit "Default" row, which rendered the same non-answer
            twice — once in the trigger before anything was chosen, once inside
            the list. No asterisk on the label is what marks it optional, the
            same way it does for Date of Birth.
          */}
          {showsWorkflowPicker && (
          <div className="space-y-2">
            <Label className="text-xs font-semibold tracking-wider uppercase text-slate-700">
              Verification Flow
            </Label>
            <Select value={workflowKey} onValueChange={setWorkflowKey}>
              {/* The <Label> above is not associated with the Radix trigger, so
                  without this the combobox has no accessible name — and on a
                  leasing org with definitions there would be two unnamed
                  comboboxes in a row. Also what lets the tests select this one
                  by name instead of by position. */}
              <SelectTrigger
                aria-label="Verification Flow"
                className="h-12 rounded-xl border-slate-200 bg-slate-50/60 focus:ring-2 focus:ring-[#7C3AED]/30 focus:border-[#7C3AED]"
              >
                <SelectValue placeholder="Select flow" />
              </SelectTrigger>
              <SelectContent>
                {workflowChoices.map((w) => (
                  <SelectItem key={w.workflow_key} value={w.workflow_key}>
                    {workflowLabel(w)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          )}

          {/*
            Leasing only. In recruiting the lane applies to everyone who
            applies to the role, so there is no per-candidate reason to give —
            and inventing recruiting reasons to fill the slot would put a
            leasing concept on a hiring form. The recruiting counterpart is the
            FLOW, which needs `display_name` on an API before it can be picked;
            until then this row is simply absent.
          */}
          {showsReason && (
          <div className="space-y-2">
            <Label className="text-xs font-semibold tracking-wider uppercase text-slate-700">
              Reason for Follow-Up <span className="text-red-500">*</span>
            </Label>
            <TooltipProvider delayDuration={150}>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="h-12 rounded-xl border-slate-200 bg-slate-50/60 focus:ring-2 focus:ring-[#7C3AED]/30 focus:border-[#7C3AED]">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {reasons.map((r) => (
                    <Tooltip key={r.value}>
                      <TooltipTrigger asChild>
                        <SelectItem value={r.value} className="pr-8">
                          <span className="inline-flex items-center gap-2">
                            {r.label}
                            <Info className="w-3.5 h-3.5 text-slate-400" aria-hidden />
                          </span>
                        </SelectItem>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs text-xs">
                        {r.tooltip}
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </SelectContent>
              </Select>
            </TooltipProvider>
          </div>
          )}


          <Button
            className="w-full h-12 rounded-xl text-white font-semibold border-0 shadow-sm hover:shadow-lg hover:-translate-y-px transition-all gap-2"
            style={{ background: "linear-gradient(135deg, #7C3AED, #6D28D9)" }}
            size="lg"
            onClick={handleSend}
            disabled={submitting}
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? "Sending..." : "Send Verification Link"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
